import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import * as routingService from '@/services/routingService'
import { settingsService } from '@/services/settingsService'
import { showError } from '@/services/toastService'
import { UI_SETTING_KEYS } from '@/services/uiSettingsRegistry'

export interface SmartSearchTrackSelection {
  workspaceId: string
  tourId: string
  trackClientId: string
}

export type MapNavigationIntent =
  | 'collection-route'
  | 'tour-list'
  | 'poi-list'
  | 'waypoint-list'
  | 'track-list'
  | 'deeplink'
  | 'back'
  | 'map'
  | 'explicit'

export type AutomaticMapMovementTarget = 'workspace-content' | 'overview-content' | 'route-item'

type LayerPanelPreferences = {
  expandedCategories?: Record<string, boolean>
  layerVisibility?: Record<string, boolean>
}

const VALID_WORKSPACE_OVERVIEW_SORTS = ['name', 'date', 'modified'] as const
type WorkspaceOverviewSortByValue = (typeof VALID_WORKSPACE_OVERVIEW_SORTS)[number]

const normalizeWorkspaceOverviewSortBy = (value: unknown): WorkspaceOverviewSortByValue =>
  typeof value === 'string' && (VALID_WORKSPACE_OVERVIEW_SORTS as readonly string[]).includes(value)
    ? (value as WorkspaceOverviewSortByValue)
    : 'modified'

export const useUiStore = defineStore('ui', () => {
  const validRoutingProfileIds = new Set(
    (routingService.ROUTING_PROFILES ?? []).map((profile) => profile.id),
  )
  const normalizeRoutingProfile = (profile: string | null | undefined) =>
    profile && validRoutingProfileIds.has(profile)
      ? profile
      : routingService.DEFAULT_ROUTING_PROFILE

  const areWaypointsVisible = ref(true)
  const arePOIsVisible = ref(true)
  const areRoutePointsVisible = ref(true)
  const arePhotosVisible = ref(true)
  const hiddenWaypointIds = ref<Set<string>>(new Set())
  const hiddenPOIIds = ref<Set<string>>(new Set())
  const hoveredWaypointId = ref<string | null>(null)
  // The three map drawers (basemap "Map type", "Map layers", user-facing "Map content") are mutually
  // exclusive — only one open at a time. They render as non-modal bottom sheets, so unlike a modal
  // they don't block opening a sibling; a single source of truth enforces the exclusivity instead.
  const activeMapDrawer = ref<'type' | 'layers' | 'content' | null>(null)
  const isMapStylePanelVisible = computed(() => activeMapDrawer.value === 'type')
  const isLayersPanelVisible = computed(() => activeMapDrawer.value === 'layers')
  // The user-facing "Map content" panel (POI categories to show + add to a collection). The
  // developer-oriented "Map layers" panel is separate.
  const isMapContentPanelVisible = computed(() => activeMapDrawer.value === 'content')
  const isAnyMapDrawerVisible = computed(() => activeMapDrawer.value !== null)
  const tourHoverSuppressionDepth = ref(0)
  const isTourHoverSuppressed = computed(() => tourHoverSuppressionDepth.value > 0)

  const defaultLayerPanelExpandedCategories: Record<string, boolean> = {
    base: true,
    terrain: false,
    vegetation: false,
    water: false,
    urban: false,
    buildings: false,
    roads: false,
    railways: false,
    aviation: false,
    trails: false,
    cycling: false,
    lifts: false,
    boundaries: false,
    pointsOfInterest: false,
    labels: false,
  }

  const layerPanelExpandedCategories = ref<Record<string, boolean>>({
    ...defaultLayerPanelExpandedCategories,
  })

  // Unified per-layer visibility. Every layer the user has explicitly toggled is stored here.
  // Category state (all/partial/none) is derived from these individual entries in mapStore.
  const layerVisibility = ref<Record<string, boolean>>({})

  // Track if we're in workspaces overview mode (showing multiple workspaces)
  const isInWorkspacesOverview = ref(false)

  // Sidebar visibility state
  const isSidebarVisible = ref(true)

  // Library panel visibility state
  const isLibraryPanelVisible = ref(true)

  // Mobile bottom panel state
  const isMobileBottomPanelExpanded = ref(false)
  const mobileBottomPanelHeight = ref(64)
  const mobileBottomPanelExpandedProgress = ref(0)
  const mobileBottomPanelHighProgress = ref(0)
  const mobileBottomPanelExpandedHeight = ref(64)
  // True when the drawer is at its tallest "high" snap (nearly full screen), so the floating create
  // FAB can hide rather than clash with the top chrome / cover the list.
  const isMobileBottomPanelHigh = ref(false)
  function setMobileBottomPanelHigh(high: boolean) {
    isMobileBottomPanelHigh.value = high
  }
  function setMobileBottomPanelHighProgress(progress: number, expandedHeight: number) {
    mobileBottomPanelHighProgress.value = Math.min(1, Math.max(0, progress))
    mobileBottomPanelExpandedHeight.value = Math.max(64, expandedHeight)
  }
  function setMobileBottomPanelExpandedProgress(progress: number) {
    mobileBottomPanelExpandedProgress.value = Math.min(1, Math.max(0, progress))
  }

  // When true, the mobile experience shows only the map (no bottom-sheet drawer) — this is
  // the default landing state on mobile, surfaced as the "Map" tab in MobileTabBar.
  const isMobileMapView = ref(true)
  function setMobileMapView(value: boolean) {
    isMobileMapView.value = value
  }

  // Immersive "Full map" mode (mobile): hides all browsing chrome (tab bar, bottom sheets,
  // create FAB, sync button) so the whole screen is the map. The round map-controls cluster
  // stays and its button is the exit. Transient — not persisted; auto-exits when a task mode
  // (track draw/edit, navigation, landing) takes over (see WorkspaceMapContainer).
  const isImmersiveMapMode = ref(false)
  function setImmersiveMapMode(value: boolean) {
    isImmersiveMapMode.value = value
  }
  function toggleImmersiveMapMode() {
    isImmersiveMapMode.value = !isImmersiveMapMode.value
  }

  // Incrementing epoch used to signal downstream map components that toolbar layout
  // transitions (mobile bottom sheet, desktop sidebar) have completed and the map
  // viewport is stable again.
  const toolbarLayoutEpoch = ref(0)

  // Library panel height (persisted, null = use computed default)
  const libraryPanelHeight = ref<number | null>(null)

  // When true, MapLibreRoot skips resize() calls (used during drag-resize)
  const mapResizeSuppressed = ref(false)

  // Current visible panel state
  const activePanel = ref<'tourDetail' | 'poiDetail' | null>(null)

  // Dialog visibility state - tracks when any modal/dialog is open
  const isDialogOpen = ref(false)

  // When true, the next mount of components that might auto-zoom (e.g., Toolbar in workspaces view)
  // should not trigger a zoom. This is used when toggling the desktop sidebar via the chevron.
  const suppressNextAutoZoom = ref(false)

  // Auto viewport adaptation state
  // When true (default), UI chrome changes like expanding/collapsing toolbars may adapt the map viewport
  // by calling zoom-to-content with updated padding. Once the user manually moves/zooms the map,
  // this toggles to false to preserve the user's chosen view until the next navigation context change.
  const autoViewportAdaptationEnabled = ref(true)

  // Static page URL restoration flag
  // When true, prevents expensive route operations during static page URL restoration
  const isRestoringFromStaticPage = ref(false)

  // Landing page overlay visibility
  const isLandingPageVisible = ref(false)

  // Track drawing state (creating new tracks)
  const isTrackDrawingActive = ref(false)

  // All Activities & Routes panel is open
  const isAllActivitiesView = ref(false)

  // Desktop sidebar panel width (updated by WorkspaceToolbar.vue via watcher)
  const toolbarSidebarWidth = ref(0)

  // Track edit mode state (editing existing tracks)
  const isTrackEditMode = ref(false)
  const editingTrackTourId = ref<string | null>(null)
  const editingTrackClientId = ref<string | null>(null)
  const editingTrackAppendDirection = ref<'end' | 'start'>('end')
  const suppressMapClickUntil = ref(0)

  // Routing profile for track drawing (persisted)
  const routingProfile = ref(
    normalizeRoutingProfile(settingsService.getSetting<string>(UI_SETTING_KEYS.routingProfile)),
  )
  // Workspace search state
  const workspaceSearchQuery = ref('')
  const workspaceSearchSortBy = ref<WorkspaceOverviewSortByValue>('modified')
  const workspaceSearchSortDirection = ref<'asc' | 'desc'>('desc')
  const workspaceOverviewSharingFilter = ref<'all' | 'personal' | 'shared'>('all')
  const workspaceOverviewOfflineFilter = ref<'all' | 'downloaded'>('all')

  function applyWorkspaceSearchPreferences(
    searchPreferences: {
      searchQuery?: string
      sortBy?: string
      sortDirection?: 'asc' | 'desc'
      sourceFilter?: 'all' | 'created' | 'shared' | 'saved'
      sharingFilter?: 'all' | 'personal' | 'private' | 'shared' | 'public' | 'bookmarks'
      offlineFilter?: 'all' | 'downloaded'
      selectedTypes?: string[]
      ownershipFilter?: string[]
    } | null,
  ) {
    if (!searchPreferences) return

    if (searchPreferences.searchQuery !== undefined) {
      workspaceSearchQuery.value = searchPreferences.searchQuery
    }
    if (searchPreferences.sortBy !== undefined) {
      workspaceSearchSortBy.value = normalizeWorkspaceOverviewSortBy(searchPreferences.sortBy)
    }
    if (searchPreferences.sortDirection !== undefined) {
      workspaceSearchSortDirection.value = searchPreferences.sortDirection
    }
    if (searchPreferences.sharingFilter !== undefined) {
      workspaceOverviewSharingFilter.value =
        searchPreferences.sharingFilter === 'personal' ||
        searchPreferences.sharingFilter === 'private'
          ? 'personal'
          : searchPreferences.sharingFilter === 'shared' ||
              searchPreferences.sharingFilter === 'public' ||
              searchPreferences.sharingFilter === 'bookmarks'
            ? 'shared'
            : 'all'
    } else if (searchPreferences.sourceFilter !== undefined) {
      workspaceOverviewSharingFilter.value =
        searchPreferences.sourceFilter === 'created'
          ? 'personal'
          : searchPreferences.sourceFilter === 'shared' ||
              searchPreferences.sourceFilter === 'saved'
            ? 'shared'
            : 'all'
    } else if (Array.isArray(searchPreferences.ownershipFilter)) {
      const legacySourceFilter = searchPreferences.ownershipFilter.find((value) =>
        ['all', 'created', 'shared', 'saved'].includes(value),
      )
      if (legacySourceFilter) {
        workspaceOverviewSharingFilter.value =
          legacySourceFilter === 'created'
            ? 'personal'
            : legacySourceFilter === 'shared' || legacySourceFilter === 'saved'
              ? 'shared'
              : 'all'
      }
    } else if (Array.isArray(searchPreferences.selectedTypes)) {
      const hasPublic = searchPreferences.selectedTypes.includes('public')
      const hasPrivate = searchPreferences.selectedTypes.includes('private')
      workspaceOverviewSharingFilter.value =
        hasPublic && !hasPrivate ? 'shared' : !hasPublic && hasPrivate ? 'personal' : 'all'
    }
    if (searchPreferences.offlineFilter !== undefined) {
      workspaceOverviewOfflineFilter.value = searchPreferences.offlineFilter
    }
  }

  function applyLayerPanelPreferences(layerPreferences: LayerPanelPreferences | null) {
    if (!layerPreferences) return

    if (layerPreferences.expandedCategories) {
      layerPanelExpandedCategories.value = {
        ...defaultLayerPanelExpandedCategories,
        ...layerPreferences.expandedCategories,
      }
    }
    if (layerPreferences.layerVisibility) {
      layerVisibility.value = { ...layerPreferences.layerVisibility }
    }
  }

  // Initialize the store and load data from localStorage
  async function init() {
    try {
      applyWorkspaceSearchPreferences(
        settingsService.getSetting(UI_SETTING_KEYS.workspaceSearchPreferences),
      )

      const savedPanelHeight = settingsService.getSetting<number>(
        UI_SETTING_KEYS.libraryPanelHeight,
      )
      if (savedPanelHeight !== null) {
        libraryPanelHeight.value = savedPanelHeight
      }

      applyLayerPanelPreferences(
        settingsService.getSetting<LayerPanelPreferences>(UI_SETTING_KEYS.mapLayerPreferences),
      )

      // Set up subscription for state changes AFTER initial data is loaded
    } catch (error) {
      showError(
        `Failed to initialize UI store: ${error instanceof Error ? error.message : String(error)}`,
      )
      throw error
    }
  }

  function toggleWaypointVisibility() {
    areWaypointsVisible.value = !areWaypointsVisible.value
  }

  function togglePOIVisibility() {
    arePOIsVisible.value = !arePOIsVisible.value
  }

  function toggleRoutePointVisibility() {
    areRoutePointsVisible.value = !areRoutePointsVisible.value
  }

  function showPanel(panel: 'tourDetail' | 'poiDetail') {
    // A detail/tour panel and a map drawer both own the mobile bottom — only one at a time.
    closeMapDrawer()
    activePanel.value = panel
  }

  function hidePanel() {
    activePanel.value = null
  }

  function setWaypointsVisible(visible: boolean) {
    areWaypointsVisible.value = visible
  }

  function setPOIsVisible(visible: boolean) {
    arePOIsVisible.value = visible
  }

  function setRoutePointsVisible(visible: boolean) {
    areRoutePointsVisible.value = visible
  }

  function togglePhotoVisibility() {
    arePhotosVisible.value = !arePhotosVisible.value
  }

  function setPhotosVisible(visible: boolean) {
    arePhotosVisible.value = visible
  }

  function setHoveredWaypoint(waypointId: string | null) {
    hoveredWaypointId.value = waypointId
  }

  function isWaypointHidden(waypointId: string) {
    return hiddenWaypointIds.value.has(waypointId)
  }

  function setWaypointHidden(waypointId: string, hidden: boolean) {
    const next = new Set(hiddenWaypointIds.value)
    if (hidden) {
      next.add(waypointId)
    } else {
      next.delete(waypointId)
    }
    hiddenWaypointIds.value = next
  }

  function toggleSingleWaypointVisibility(waypointId: string) {
    setWaypointHidden(waypointId, !isWaypointHidden(waypointId))
  }

  function isPOIHidden(poiId: string) {
    return hiddenPOIIds.value.has(poiId)
  }

  function setPOIHidden(poiId: string, hidden: boolean) {
    const next = new Set(hiddenPOIIds.value)
    if (hidden) {
      next.add(poiId)
    } else {
      next.delete(poiId)
    }
    hiddenPOIIds.value = next
  }

  function toggleSinglePOIVisibility(poiId: string) {
    setPOIHidden(poiId, !isPOIHidden(poiId))
  }

  function openMapDrawer(drawer: 'type' | 'layers' | 'content') {
    if (isMobileBottomPanelExpanded.value) {
      collapseMobileBottomPanel()
    }
    activeMapDrawer.value = drawer
  }

  function closeMapDrawer(drawer?: 'type' | 'layers' | 'content') {
    if (!drawer || activeMapDrawer.value === drawer) activeMapDrawer.value = null
  }

  function toggleMapDrawer(drawer: 'type' | 'layers' | 'content') {
    if (activeMapDrawer.value === drawer) activeMapDrawer.value = null
    else openMapDrawer(drawer)
  }

  function toggleLayersPanel() {
    toggleMapDrawer('layers')
  }

  function showLayersPanel() {
    openMapDrawer('layers')
  }

  function hideLayersPanel() {
    closeMapDrawer('layers')
  }

  function toggleMapContentPanel() {
    toggleMapDrawer('content')
  }

  function hideMapContentPanel() {
    closeMapDrawer('content')
  }

  function toggleMapStylePanel() {
    toggleMapDrawer('type')
  }

  function showMapStylePanel() {
    openMapDrawer('type')
  }

  function hideMapStylePanel() {
    closeMapDrawer('type')
  }

  function showTourDetailPanel() {
    showPanel('tourDetail')
  }

  function hideTourDetailPanel() {
    hidePanel()
  }

  function showPOIDetailPanel() {
    showPanel('poiDetail')
  }

  function hidePOIDetailPanel() {
    hidePanel()
  }

  function toggleSidebar() {
    isSidebarVisible.value = !isSidebarVisible.value
  }

  function showSidebar() {
    isSidebarVisible.value = true
  }

  function hideSidebar() {
    isSidebarVisible.value = false
  }

  function toggleLibraryPanel() {
    isLibraryPanelVisible.value = !isLibraryPanelVisible.value
  }

  function showLibraryPanel() {
    isLibraryPanelVisible.value = true
  }

  function hideLibraryPanel() {
    isLibraryPanelVisible.value = false
  }

  function setWorkspacesOverview(isOverview: boolean) {
    isInWorkspacesOverview.value = isOverview
    // When entering workspaces overview (search mode), always hide any active panel
    if (isOverview && activePanel.value !== null) {
      activePanel.value = null
    }
  }

  function toggleMobileBottomPanel() {
    const nextExpanded = !isMobileBottomPanelExpanded.value
    if (nextExpanded && isLayersPanelVisible.value) {
      hideLayersPanel()
    }
    isMobileBottomPanelExpanded.value = nextExpanded
  }

  function expandMobileBottomPanel() {
    if (isLayersPanelVisible.value) {
      hideLayersPanel()
    }
    isMobileBottomPanelExpanded.value = true
  }

  function collapseMobileBottomPanel() {
    isMobileBottomPanelExpanded.value = false
  }

  function setMobileBottomPanelHeight(height: number) {
    mobileBottomPanelHeight.value = Number.isFinite(height) && height > 0 ? height : 64
  }

  function markToolbarLayoutStable() {
    toolbarLayoutEpoch.value += 1
  }

  function setDialogOpen(isOpen: boolean) {
    isDialogOpen.value = isOpen
  }

  function setSuppressNextAutoZoom(value: boolean) {
    suppressNextAutoZoom.value = value
  }

  function consumeSuppressNextAutoZoom(): boolean {
    const shouldSuppress = suppressNextAutoZoom.value
    suppressNextAutoZoom.value = false
    return shouldSuppress
  }

  function enableAutoViewportAdaptation() {
    autoViewportAdaptationEnabled.value = true
  }

  function disableAutoViewportAdaptation() {
    autoViewportAdaptationEnabled.value = false
  }

  function setAutoViewportAdaptationEnabled(value: boolean) {
    autoViewportAdaptationEnabled.value = value
  }

  function setRestoringFromStaticPage(value: boolean) {
    isRestoringFromStaticPage.value = value
  }

  function setIsTrackDrawingActive(active: boolean) {
    isTrackDrawingActive.value = active
    if (active) exitTrackEditMode()
    else isMapElementsHiddenByButton.value = false
  }

  function enterTrackEditMode(tourId: string, trackClientId: string) {
    isTrackDrawingActive.value = false
    isTrackEditMode.value = true
    editingTrackTourId.value = tourId
    editingTrackClientId.value = trackClientId
    editingTrackAppendDirection.value = 'end'
  }

  function exitTrackEditMode() {
    isTrackEditMode.value = false
    editingTrackTourId.value = null
    editingTrackClientId.value = null
    editingTrackAppendDirection.value = 'end'
    isMapElementsHiddenByButton.value = false
  }

  function handleTrackDeleted(tourId: string, trackClientId: string) {
    if (
      isTrackEditMode.value &&
      editingTrackTourId.value === tourId &&
      editingTrackClientId.value === trackClientId
    ) {
      exitTrackEditMode()
      return
    }

    // Draw mode does not currently persist the active drawing track id in ui state.
    // Closing the draw panel avoids leaving stale create-route UI open after a track delete.
    if (isTrackDrawingActive.value) {
      isTrackDrawingActive.value = false
    }
  }

  function setEditingTrackAppendDirection(direction: 'end' | 'start') {
    editingTrackAppendDirection.value = direction
  }

  function suppressNextMapClick(durationMs = 300) {
    suppressMapClickUntil.value = Date.now() + durationMs
  }

  function shouldSuppressNextMapClick() {
    return Date.now() < suppressMapClickUntil.value
  }

  function setRoutingProfile(profile: string, options: { persist?: boolean } = {}) {
    const normalizedProfile = normalizeRoutingProfile(profile)
    routingProfile.value = normalizedProfile
    if (options.persist === false) {
      return
    }

    settingsService.saveSetting(UI_SETTING_KEYS.routingProfile, normalizedProfile)
  }

  function showLandingPage() {
    isLandingPageVisible.value = true
  }

  function hideLandingPage() {
    isLandingPageVisible.value = false
  }

  // Workspace search state management
  function saveWorkspaceSearchPreferences() {
    try {
      settingsService.saveSetting(UI_SETTING_KEYS.workspaceSearchPreferences, {
        searchQuery: workspaceSearchQuery.value,
        sortBy: workspaceSearchSortBy.value,
        sortDirection: workspaceSearchSortDirection.value,
        sharingFilter: workspaceOverviewSharingFilter.value,
        offlineFilter: workspaceOverviewOfflineFilter.value,
      })
    } catch (error) {
      showError(
        `Failed to save search preferences: ${error instanceof Error ? error.message : String(error)}`,
      )
    }
  }

  function setWorkspaceSearchQuery(query: string) {
    workspaceSearchQuery.value = query
    saveWorkspaceSearchPreferences()
  }

  function setWorkspaceSearchSortBy(sortBy: WorkspaceOverviewSortByValue) {
    workspaceSearchSortBy.value = sortBy
    saveWorkspaceSearchPreferences()
  }

  function setWorkspaceSearchSortDirection(direction: 'asc' | 'desc') {
    workspaceSearchSortDirection.value = direction
    saveWorkspaceSearchPreferences()
  }

  function setWorkspaceOverviewSharingFilter(filter: 'all' | 'personal' | 'shared') {
    workspaceOverviewSharingFilter.value = filter
    saveWorkspaceSearchPreferences()
  }

  function setWorkspaceOverviewOfflineFilter(filter: 'all' | 'downloaded') {
    workspaceOverviewOfflineFilter.value = filter
    saveWorkspaceSearchPreferences()
  }

  function saveLayerPanelPreferences() {
    try {
      const preferences: LayerPanelPreferences = {
        expandedCategories: layerPanelExpandedCategories.value,
        layerVisibility: layerVisibility.value,
      }
      settingsService.saveSetting(UI_SETTING_KEYS.mapLayerPreferences, preferences)
    } catch (error) {
      showError(
        `Failed to save map layer preferences: ${
          error instanceof Error ? error.message : String(error)
        }`,
      )
    }
  }

  function setLayerPanelCategoryExpanded(categoryId: string, expanded: boolean) {
    if (layerPanelExpandedCategories.value[categoryId] === expanded) return

    layerPanelExpandedCategories.value = {
      ...layerPanelExpandedCategories.value,
      [categoryId]: expanded,
    }
    saveLayerPanelPreferences()
  }

  function setLayerVisibility(layerId: string, visible: boolean) {
    if (layerVisibility.value[layerId] === visible) return

    layerVisibility.value = {
      ...layerVisibility.value,
      [layerId]: visible,
    }
    saveLayerPanelPreferences()
  }

  function setLibraryPanelHeight(height: number) {
    libraryPanelHeight.value = height
    settingsService.saveSetting(UI_SETTING_KEYS.libraryPanelHeight, height)
  }

  settingsService.subscribe(({ key, value, source }) => {
    if (source !== 'remote') return

    if (key === UI_SETTING_KEYS.routingProfile) {
      routingProfile.value = normalizeRoutingProfile(value as string | null | undefined)
    } else if (key === UI_SETTING_KEYS.workspaceSearchPreferences) {
      applyWorkspaceSearchPreferences(
        value as {
          searchQuery?: string
          sortBy?: string
          sortDirection?: 'asc' | 'desc'
          sourceFilter?: 'all' | 'created' | 'shared' | 'saved'
          sharingFilter?: 'all' | 'personal' | 'private' | 'shared' | 'public' | 'bookmarks'
          offlineFilter?: 'all' | 'downloaded'
          selectedTypes?: string[]
          ownershipFilter?: string[]
        } | null,
      )
    } else if (key === UI_SETTING_KEYS.mapLayerPreferences) {
      applyLayerPanelPreferences(value as LayerPanelPreferences | null)
    } else if (key === UI_SETTING_KEYS.libraryPanelHeight) {
      libraryPanelHeight.value = typeof value === 'number' ? value : null
    }
  })

  function suppressTourHover() {
    tourHoverSuppressionDepth.value += 1
  }

  function releaseTourHoverSuppression() {
    if (tourHoverSuppressionDepth.value === 0) {
      return
    }
    tourHoverSuppressionDepth.value -= 1
  }

  const pendingWorkspaceShareOpen = ref(false)
  const mapNavigationIntent = ref<MapNavigationIntent | null>(null)
  const preserveMobileBottomPanelStateOnNextOverviewEntry = ref(false)

  function requestWorkspaceShareOpen() {
    pendingWorkspaceShareOpen.value = true
  }

  function consumeWorkspaceShareOpen() {
    pendingWorkspaceShareOpen.value = false
  }

  function setMapNavigationIntent(intent: MapNavigationIntent | null) {
    mapNavigationIntent.value = intent
  }

  function beginMapNavigation(
    intent: MapNavigationIntent | null,
    options: { preserveMobilePanelState?: boolean } = {},
  ) {
    setMapNavigationIntent(intent)
    // Panel-state preservation is independent of camera policy: surface switches (tab bar,
    // desktop switcher) keep the drawer state but must not suppress the fit-on-open zoom,
    // so they pass the option explicitly instead of hijacking the 'map' intent.
    preserveMobileBottomPanelStateOnNextOverviewEntry.value =
      options.preserveMobilePanelState ?? (intent === 'back' || intent === 'map')
    if (intent === 'back' || intent === 'map') {
      disableAutoViewportAdaptation()
    }
  }

  function consumeMapNavigationIntent(expected?: MapNavigationIntent) {
    const currentIntent = mapNavigationIntent.value
    if (expected && currentIntent !== expected) {
      return null
    }
    mapNavigationIntent.value = null
    return currentIntent
  }

  function shouldAutoMoveMap(
    target: AutomaticMapMovementTarget,
    options: { hasPendingItemRoute?: boolean } = {},
  ) {
    if (target === 'overview-content') {
      return mapNavigationIntent.value !== 'back'
    }

    if (target === 'route-item') {
      return mapNavigationIntent.value !== 'back' && mapNavigationIntent.value !== 'map'
    }

    if (options.hasPendingItemRoute) {
      return false
    }

    return mapNavigationIntent.value !== 'back' && mapNavigationIntent.value !== 'map'
  }

  function finalizeAutomaticMapMovement(
    target: AutomaticMapMovementTarget,
    options: { hasPendingItemRoute?: boolean } = {},
  ) {
    if (target === 'overview-content') {
      consumeMapNavigationIntent('back')
      return
    }

    if (target === 'route-item') {
      if (mapNavigationIntent.value === 'deeplink') {
        consumeMapNavigationIntent('deeplink')
      } else if (mapNavigationIntent.value === 'back') {
        consumeMapNavigationIntent('back')
      } else if (mapNavigationIntent.value === 'map') {
        consumeMapNavigationIntent('map')
      }
      return
    }

    if (options.hasPendingItemRoute) {
      return
    }

    if (mapNavigationIntent.value === 'collection-route') {
      consumeMapNavigationIntent('collection-route')
    } else if (mapNavigationIntent.value === 'back') {
      consumeMapNavigationIntent('back')
    } else if (mapNavigationIntent.value === 'map') {
      consumeMapNavigationIntent('map')
    }
  }

  function runIntentDrivenMapMovement(
    intent: Extract<
      MapNavigationIntent,
      'tour-list' | 'poi-list' | 'waypoint-list' | 'track-list' | 'explicit'
    >,
    move: () => void,
  ) {
    beginMapNavigation(intent)
    move()
    disableAutoViewportAdaptation()
    consumeMapNavigationIntent(intent)
  }

  function shouldPreserveMobileBottomPanelStateOnOverviewEntry() {
    return preserveMobileBottomPanelStateOnNextOverviewEntry.value
  }

  function consumeMobileBottomPanelStatePreservationOnOverviewEntry() {
    const shouldPreserve = preserveMobileBottomPanelStateOnNextOverviewEntry.value
    preserveMobileBottomPanelStateOnNextOverviewEntry.value = false
    return shouldPreserve
  }

  const isMapElementsHiddenByKey = ref(false)
  const isMapElementsHiddenByButton = ref(false)
  const areMapElementsForceHidden = computed(
    () => isMapElementsHiddenByKey.value || isMapElementsHiddenByButton.value,
  )

  function setMapElementsHiddenByKey(hidden: boolean) {
    isMapElementsHiddenByKey.value = hidden
  }

  function toggleMapElementsHiddenByButton() {
    isMapElementsHiddenByButton.value = !isMapElementsHiddenByButton.value
  }

  const isSmartSearchOpen = ref(false)
  const pendingSmartSearchTrackSelection = ref<SmartSearchTrackSelection | null>(null)

  // The mobile Profile account sheet (UserMenu → ProfileSheet). Tracked centrally so the
  // create FAB and other map-shell affordances can hide while it is open, like the other
  // mobile tab surfaces.
  const isProfileSheetOpen = ref(false)
  function setProfileSheetOpen(open: boolean) {
    isProfileSheetOpen.value = open
  }

  function openSmartSearch() {
    isSmartSearchOpen.value = true
  }

  function closeSmartSearch() {
    isSmartSearchOpen.value = false
  }

  function queueSmartSearchTrackSelection(selection: SmartSearchTrackSelection) {
    pendingSmartSearchTrackSelection.value = selection
  }

  function clearSmartSearchTrackSelection() {
    pendingSmartSearchTrackSelection.value = null
  }

  return {
    areWaypointsVisible,
    arePOIsVisible,
    hiddenWaypointIds,
    hiddenPOIIds,
    areRoutePointsVisible,
    arePhotosVisible,
    hoveredWaypointId,
    activeMapDrawer,
    isMapStylePanelVisible,
    isLayersPanelVisible,
    isMapContentPanelVisible,
    isAnyMapDrawerVisible,
    isTourHoverSuppressed,
    layerPanelExpandedCategories,
    layerVisibility,
    activePanel,
    isInWorkspacesOverview,
    isSidebarVisible,
    isLibraryPanelVisible,
    isMobileBottomPanelExpanded,
    mobileBottomPanelHeight,
    mobileBottomPanelExpandedProgress,
    setMobileBottomPanelExpandedProgress,
    mobileBottomPanelHighProgress,
    mobileBottomPanelExpandedHeight,
    isMobileBottomPanelHigh,
    setMobileBottomPanelHigh,
    setMobileBottomPanelHighProgress,
    isMobileMapView,
    setMobileMapView,
    isImmersiveMapMode,
    setImmersiveMapMode,
    toggleImmersiveMapMode,
    toolbarLayoutEpoch,
    isDialogOpen,
    suppressNextAutoZoom,
    autoViewportAdaptationEnabled,
    isRestoringFromStaticPage,
    isTrackDrawingActive,
    isAllActivitiesView,
    toolbarSidebarWidth,
    isTrackEditMode,
    editingTrackTourId,
    editingTrackClientId,
    editingTrackAppendDirection,
    suppressMapClickUntil,
    routingProfile,
    isLandingPageVisible,
    workspaceSearchQuery,
    workspaceSearchSortBy,
    workspaceSearchSortDirection,
    workspaceOverviewSharingFilter,
    workspaceOverviewOfflineFilter,
    init,
    toggleWaypointVisibility,
    togglePOIVisibility,
    toggleRoutePointVisibility,
    setWaypointsVisible,
    setPOIsVisible,
    setRoutePointsVisible,
    togglePhotoVisibility,
    setPhotosVisible,
    setHoveredWaypoint,
    isWaypointHidden,
    setWaypointHidden,
    toggleSingleWaypointVisibility,
    isPOIHidden,
    setPOIHidden,
    toggleSinglePOIVisibility,
    toggleLayersPanel,
    showLayersPanel,
    hideLayersPanel,
    toggleMapContentPanel,
    hideMapContentPanel,
    toggleMapStylePanel,
    showMapStylePanel,
    hideMapStylePanel,
    closeMapDrawer,
    toggleSidebar,
    showSidebar,
    hideSidebar,
    toggleLibraryPanel,
    showLibraryPanel,
    hideLibraryPanel,
    showPanel,
    hidePanel,
    showTourDetailPanel,
    hideTourDetailPanel,
    showPOIDetailPanel,
    hidePOIDetailPanel,
    setWorkspacesOverview,
    toggleMobileBottomPanel,
    expandMobileBottomPanel,
    collapseMobileBottomPanel,
    markToolbarLayoutStable,
    setDialogOpen,
    setSuppressNextAutoZoom,
    consumeSuppressNextAutoZoom,
    enableAutoViewportAdaptation,
    disableAutoViewportAdaptation,
    setAutoViewportAdaptationEnabled,
    setRestoringFromStaticPage,
    setIsTrackDrawingActive,
    enterTrackEditMode,
    exitTrackEditMode,
    handleTrackDeleted,
    setEditingTrackAppendDirection,
    suppressNextMapClick,
    shouldSuppressNextMapClick,
    setRoutingProfile,
    showLandingPage,
    hideLandingPage,
    setWorkspaceSearchQuery,
    setWorkspaceSearchSortBy,
    setWorkspaceSearchSortDirection,
    setWorkspaceOverviewSharingFilter,
    setWorkspaceOverviewOfflineFilter,
    setMobileBottomPanelHeight,
    setLayerPanelCategoryExpanded,
    setLayerVisibility,
    libraryPanelHeight,
    setLibraryPanelHeight,
    mapResizeSuppressed,
    suppressTourHover,
    releaseTourHoverSuppression,
    pendingWorkspaceShareOpen,
    requestWorkspaceShareOpen,
    consumeWorkspaceShareOpen,
    mapNavigationIntent,
    beginMapNavigation,
    setMapNavigationIntent,
    consumeMapNavigationIntent,
    shouldAutoMoveMap,
    finalizeAutomaticMapMovement,
    runIntentDrivenMapMovement,
    shouldPreserveMobileBottomPanelStateOnOverviewEntry,
    consumeMobileBottomPanelStatePreservationOnOverviewEntry,
    isMapElementsHiddenByButton,
    areMapElementsForceHidden,
    setMapElementsHiddenByKey,
    toggleMapElementsHiddenByButton,
    isSmartSearchOpen,
    openSmartSearch,
    closeSmartSearch,
    isProfileSheetOpen,
    setProfileSheetOpen,
    pendingSmartSearchTrackSelection,
    queueSmartSearchTrackSelection,
    clearSmartSearchTrackSelection,
  }
})

// Export a minimal UiStoreMock type for use in tests
